import { RESULTS_PER_PAGE } from "./constants"
import type { SearchFeedback } from "./history"
import type { CropRect, ImageDetection, ImageSearchResponse } from "./types"
import type { RecoveryRequest } from "./useImageSearch"

export interface PhotoSearchSnapshot {
  photoId: string
  file: File
  searchRequest: { image: File; autoCrop: boolean; crop: CropRect | null } | null
  results: ImageSearchResponse | null
  detection: ImageDetection | null
  selectedObject: string | null
  crop: CropRect | null
  selectedCategories: number[]
  showLessSimilar: boolean
  resultLimit: number
  savedFeedback: SearchFeedback | null
  savedError: "processing" | "unavailable" | null
  errorKey: "invalid" | "unavailable" | null
  inputError: string
  pending: boolean
  recoveryRequest: RecoveryRequest | null
  recoveryDeadline: number | null
}

interface Entry {
  userId: number | undefined
  snapshot: PhotoSearchSnapshot
  uploadRequested: boolean
}

// File objects stay in this tab's memory, never in persistent storage or router state.
const entries = new Map<string, Entry>()
const deletedPhotos = new Set<string>()
const discardedEntries = new Set<string>()
const feedbackListeners = new Map<string, Set<(feedback: SearchFeedback | null) => void>>()
let navigationResetPending = false
let generation = 0

function retainInvalidatedId(ids: Set<string>, id: string) {
  ids.delete(id)
  ids.add(id)
  while (ids.size > 32) ids.delete(ids.values().next().value!)
}

export function invalidatePhotoSearchNavigation() {
  navigationResetPending = true
  clearPhotoSearchSnapshots()
}

export function needsPhotoSearchNavigationReset() {
  return navigationResetPending
}

export function acknowledgePhotoSearchNavigationReset() {
  navigationResetPending = false
}

export function isPhotoId(id: string): boolean {
  return /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(id)
}

export function snapshotGeneration(): number {
  return generation
}

export function getPhotoSearchSnapshot(
  userId: number | undefined,
  entryId: string,
  photoId: string | null
): PhotoSearchSnapshot | null {
  const entry = entries.get(entryId)
  if (!entry || entry.userId !== userId || entry.snapshot.photoId !== photoId) return null
  entries.delete(entryId)
  entries.set(entryId, entry)
  return entry.snapshot
}

export function savePhotoSearchSnapshot(
  userId: number | undefined,
  entryId: string,
  snapshot: PhotoSearchSnapshot,
  ownerGeneration: number,
  uploadRequested = false
): boolean {
  if (
    ownerGeneration !== generation ||
    discardedEntries.has(entryId) ||
    deletedPhotos.has(`${userId}:${snapshot.photoId}`)
  ) {
    return false
  }
  const queued = entries.get(entryId)?.uploadRequested ?? false
  entries.delete(entryId)
  entries.set(entryId, { userId, snapshot, uploadRequested: uploadRequested || queued })
  while (entries.size > 4) entries.delete(entries.keys().next().value!)
  return true
}

export function takePhotoUpload(userId: number | undefined, entryId: string): boolean {
  const entry = entries.get(entryId)
  if (!entry || entry.userId !== userId || !entry.uploadRequested) return false
  entry.uploadRequested = false
  return true
}

export function newPhotoSearchSnapshot(photoId: string, file: File): PhotoSearchSnapshot {
  return {
    photoId,
    file,
    searchRequest: { image: file, autoCrop: true, crop: null },
    results: null,
    detection: null,
    selectedObject: null,
    crop: null,
    selectedCategories: [],
    showLessSimilar: false,
    resultLimit: RESULTS_PER_PAGE,
    savedFeedback: null,
    savedError: null,
    errorKey: null,
    inputError: "",
    pending: false,
    recoveryRequest: null,
    recoveryDeadline: null,
  }
}

export function discardPhotoSearchEntry(entryId: string) {
  entries.delete(entryId)
  retainInvalidatedId(discardedEntries, entryId)
}

export function deletePhotoSearchSnapshots(userId: number | undefined, photoId: string) {
  retainInvalidatedId(deletedPhotos, `${userId}:${photoId}`)
  for (const [id, entry] of entries) {
    if (entry.userId === userId && entry.snapshot.photoId === photoId) entries.delete(id)
  }
}

export function subscribePhotoSearchFeedback(
  entryId: string,
  listener: (feedback: SearchFeedback | null) => void
) {
  const listeners = feedbackListeners.get(entryId) ?? new Set()
  listeners.add(listener)
  feedbackListeners.set(entryId, listeners)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) feedbackListeners.delete(entryId)
  }
}

export function updatePhotoSearchFeedback(
  userId: number | undefined,
  entryId: string,
  attemptId: string,
  feedback: SearchFeedback | null,
  ownerGeneration: number
) {
  const entry = entries.get(entryId)
  // Never resurrect an evicted/deleted entry or attach a late response to a new attempt.
  if (!entry || entry.userId !== userId || entry.snapshot.results?.attempt_id !== attemptId) return
  if (
    savePhotoSearchSnapshot(
      userId,
      entryId,
      { ...entry.snapshot, savedFeedback: feedback },
      ownerGeneration
    )
  ) {
    feedbackListeners.get(entryId)?.forEach((listener) => listener(feedback))
  }
}

export function clearPhotoSearchSnapshots() {
  generation += 1
  entries.clear()
  deletedPhotos.clear()
  discardedEntries.clear()
}
